import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import {
  Button,
  Checkbox,
  Field,
  FieldContent,
  FieldError,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@@/ui/primitives';
import { XIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { ConditionMode, ConditionOperator, SchemaFieldType, type Field as FieldType } from '../../schemas/entity';
import clsx from 'clsx';

export function ConditionsEditor({
  allFields,
  currentFieldName,
  isLocked
}: {
  allFields?: Record<string, FieldType>;
  currentFieldName?: string;
  isLocked: boolean;
}) {
  const form = useFormContext();
  const {
    fields: groups,
    append: appendGroup,
    replace: replaceGroups,
    remove: removeGroup
  } = useFieldArray({
    control: form.control,
    name: 'conditions.groups'
  });

  const fieldConditions = form.watch('conditions');

  const availableFields = Object.entries(allFields || {})
    .filter(([fieldName, field]) => {
      const isCurrentField = fieldName === currentFieldName;
      const isPicklistOrBoolean =
        field.definition.type === SchemaFieldType.PICK_LIST || field.definition.type === SchemaFieldType.BOOLEAN;
      return !isCurrentField && isPicklistOrBoolean;
    })
    .map(([fieldName, field]) => ({
      name: fieldName,
      type: field.definition.type,
      options: field.definition.type === SchemaFieldType.PICK_LIST ? field.definition.options : []
    }));

  const getFieldOptions = (fieldName: string | undefined): string[] => {
    if (!fieldName || !allFields?.[fieldName]) return [];
    const field = allFields[fieldName];
    if (field.definition.type === SchemaFieldType.PICK_LIST) {
      return field.definition.options;
    }
    return [];
  };

  const getFieldType = (fieldName: string | undefined): SchemaFieldType | undefined => {
    if (!fieldName || !allFields?.[fieldName]) return undefined;
    return allFields[fieldName].definition.type;
  };

  const handleAddGroup = () => {
    appendGroup({
      mode: ConditionMode.AND,
      conditions: [
        {
          field: '',
          operator: ConditionOperator.EQUALS,
          value: false
        }
      ]
    });
  };

  const handleClearConditions = () => {
    replaceGroups([]);
  };

  return (
    <div className={clsx('flex flex-col', { 'opacity-50 pointer-events-none cursor-auto': isLocked })}>
      <h4 className='text-sm font-semibold text-neutral-950 mb-1'>Conditions</h4>
      <p className='text-xs text-neutral-600 mb-4'>Show this field depending on Pick List or Boolean fields.</p>

      {(!fieldConditions || groups.length === 0) && (
        <Button
          type='button'
          variant='secondary'
          size='sm'
          className='text-sm! font-medium! self-start'
          onClick={handleAddGroup}
          disabled={availableFields.length === 0}
        >
          <PlusIcon className='size-3.5' />
          Add Group
        </Button>
      )}

      {fieldConditions && groups.length > 0 && (
        <div className='flex flex-col gap-2'>
          {groups.map((group, groupIndex) => (
            <ConditionGroupEditor
              key={group.id}
              groupIndex={groupIndex}
              availableFields={availableFields}
              getFieldOptions={getFieldOptions}
              getFieldType={getFieldType}
              onRemove={() => removeGroup(groupIndex)}
              canRemove={groups.length > 1}
            />
          ))}

          <div className='flex gap-2'>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='text-sm! font-medium!'
              onClick={handleAddGroup}
              disabled={groups.length >= 1} // Limit to 1 group for now
            >
              <PlusIcon className='size-3.5' />
              Add Group
            </Button>

            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='text-sm! font-medium! gap-2'
              onClick={handleClearConditions}
            >
              <Trash2Icon className='size-3.5' />
              Clear
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConditionGroupEditor({
  groupIndex,
  availableFields,
  getFieldOptions,
  getFieldType,
  onRemove,
  canRemove
}: {
  groupIndex: number;
  availableFields: Array<{
    name: string;
    type: SchemaFieldType;
    options: string[];
  }>;
  getFieldOptions: (fieldName: string | undefined) => string[];
  getFieldType: (fieldName: string | undefined) => SchemaFieldType | undefined;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const form = useFormContext();
  const {
    fields: conditions,
    append: appendCondition,
    remove: removeCondition
  } = useFieldArray({
    control: form.control,
    name: `conditions.groups.${groupIndex}.conditions`
  });

  const handleAddCondition = () => {
    appendCondition({
      field: '',
      operator: ConditionOperator.EQUALS,
      value: false
    });
  };

  return (
    <div className='flex flex-col gap-3 border border-neutral-200 rounded-md p-2 px-3 bg-neutral-50'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium text-neutral-600'>Group {groupIndex + 1}</span>
          <Controller
            name={`conditions.groups.${groupIndex}.mode`}
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className='text-sm! font-medium! bg-white text-neutral-950 h-8! shadow-none'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className='text-sm font-medium font-sans' position='item-aligned'>
                  <SelectItem value={ConditionMode.AND}>And</SelectItem>
                  <SelectItem value={ConditionMode.OR}>Or</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {canRemove && (
          <Button
            type='button'
            variant='ghost'
            size='xs'
            onClick={onRemove}
            aria-label={`Remove group ${groupIndex + 1}`}
          >
            <XIcon className='size-3.5' />
          </Button>
        )}
      </div>

      <div className='flex flex-col gap-2'>
        {conditions.map((condition, conditionIndex) => (
          <ConditionEditor
            key={condition.id}
            groupIndex={groupIndex}
            conditionIndex={conditionIndex}
            availableFields={availableFields}
            getFieldOptions={getFieldOptions}
            getFieldType={getFieldType}
            onRemove={() => removeCondition(conditionIndex)}
            canRemove={conditions.length > 1}
          />
        ))}
      </div>

      <div>
        <Button
          type='button'
          variant='secondary'
          size='sm'
          className='text-sm! font-medium!'
          onClick={handleAddCondition}
          disabled={conditions.length >= 5}
        >
          <PlusIcon className='size-3.5' />
          Condition
        </Button>
      </div>
    </div>
  );
}

function ConditionEditor({
  groupIndex,
  conditionIndex,
  availableFields,
  getFieldOptions,
  getFieldType,
  onRemove,
  canRemove
}: {
  groupIndex: number;
  conditionIndex: number;
  availableFields: Array<{
    name: string;
    type: SchemaFieldType;
    options: string[];
  }>;
  getFieldOptions: (fieldName: string | undefined) => string[];
  getFieldType: (fieldName: string | undefined) => SchemaFieldType | undefined;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const form = useFormContext();
  const basePath = `conditions.groups.${groupIndex}.conditions.${conditionIndex}`;

  const selectedField = form.watch(`${basePath}.field`);
  const selectedOperator = form.watch(`${basePath}.operator`);
  const fieldType = getFieldType(selectedField);
  const options = getFieldOptions(selectedField);

  const handleFieldChange = (fieldName: string) => {
    const newFieldType = getFieldType(fieldName);

    form.setValue(`${basePath}.field`, fieldName);

    if (newFieldType === SchemaFieldType.BOOLEAN) {
      form.setValue(`${basePath}.operator`, ConditionOperator.EQUALS);
      form.setValue(`${basePath}.value`, false);
    } else if (newFieldType === SchemaFieldType.PICK_LIST) {
      form.setValue(`${basePath}.operator`, ConditionOperator.IN);
      form.setValue(`${basePath}.value`, []);
    }
  };

  return (
    <div className='flex items-center gap-2'>
      <Controller
        name={`${basePath}.field`}
        control={form.control}
        render={({ field, fieldState }) => (
          <Field orientation='horizontal' className='w-auto' data-invalid={fieldState.invalid}>
            <FieldContent className='flex-none!'>
              <Select value={field.value} onValueChange={handleFieldChange}>
                <SelectTrigger
                  className={clsx('text-sm! font-medium! bg-white text-neutral-950 shadow-none h-8', {
                    'border-red-950': fieldState.invalid
                  })}
                >
                  <SelectValue placeholder='Select field...' />
                </SelectTrigger>
                <SelectContent className='font-sans text-sm font-medium' position='item-aligned'>
                  {availableFields.map((f) => (
                    <SelectItem key={f.name} value={f.name}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </FieldContent>
          </Field>
        )}
      />

      {fieldType === SchemaFieldType.PICK_LIST && (
        <>
          <Controller
            name={`${basePath}.operator`}
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className='text-sm! font-medium! bg-white text-neutral-950 shadow-none w-24 h-8'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className='font-sans text-sm font-medium' position='item-aligned'>
                  <SelectItem value={ConditionOperator.EQUALS}>Equals</SelectItem>
                  <SelectItem value={ConditionOperator.IN}>In</SelectItem>
                </SelectContent>
              </Select>
            )}
          />

          {selectedOperator === ConditionOperator.IN ? (
            <Controller
              name={`${basePath}.value`}
              control={form.control}
              render={({ field, fieldState }) => (
                <Field orientation='horizontal' data-invalid={fieldState.invalid} className='flex-1'>
                  <FieldContent>
                    <div className='border border-neutral-200 rounded-md p-2 bg-white max-h-32 overflow-y-auto'>
                      <div className='flex flex-col gap-1.5'>
                        {options.map((option) => {
                          const isChecked = Array.isArray(field.value) ? field.value.includes(option) : false;

                          return (
                            <div key={option} className='flex items-center gap-2'>
                              <Checkbox
                                id={`${basePath}-${option}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  const currentValues = Array.isArray(field.value) ? field.value : [];

                                  if (checked) {
                                    field.onChange([...currentValues, option]);
                                  } else {
                                    field.onChange(currentValues.filter((v: string) => v !== option));
                                  }
                                }}
                              />
                              <Label
                                htmlFor={`${basePath}-${option}`}
                                className='text-sm text-neutral-950 cursor-pointer'
                              >
                                {option}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </FieldContent>
                </Field>
              )}
            />
          ) : (
            <Controller
              name={`${basePath}.value`}
              control={form.control}
              render={({ field, fieldState }) => (
                <Field orientation='horizontal' data-invalid={fieldState.invalid} className='flex-1'>
                  <FieldContent>
                    <Select
                      value={Array.isArray(field.value) ? (field.value[0] ?? '') : ''}
                      onValueChange={(value) => field.onChange([value])}
                    >
                      <SelectTrigger
                        className={clsx('text-sm! font-medium! bg-white text-neutral-950 shadow-none h-8', {
                          'border-red-950': fieldState.invalid
                        })}
                      >
                        <SelectValue placeholder='Select value...' />
                      </SelectTrigger>
                      <SelectContent className='font-sans text-sm font-medium' position='item-aligned'>
                        {options.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </FieldContent>
                </Field>
              )}
            />
          )}
        </>
      )}

      {fieldType === SchemaFieldType.BOOLEAN && (
        <>
          <span className='text-sm text-neutral-600 self-center'>is</span>
          <Controller
            name={`${basePath}.value`}
            control={form.control}
            render={({ field }) => (
              <Select value={field.value?.toString()} onValueChange={(value) => field.onChange(value === 'true')}>
                <SelectTrigger className='text-sm! font-medium! bg-white text-neutral-950 shadow-none w-24 h-8'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className='font-sans text-sm font-medium' position='item-aligned'>
                  <SelectItem value='true'>True</SelectItem>
                  <SelectItem value='false'>False</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </>
      )}

      {canRemove && (
        <Button
          type='button'
          variant='ghost'
          size='xs'
          onClick={onRemove}
          aria-label={`Remove condition ${conditionIndex + 1}`}
          className='mt-0.5'
        >
          <XIcon className='size-3.5' />
        </Button>
      )}
    </div>
  );
}
